<script setup lang="ts">
import SignIn from "./SignIn.vue";
import SignUp from "./SignUp.vue";
import { ref, onMounted, onUnmounted } from "vue";
import { useAuthStore, useTokenStore, useAuthModal } from "@/features/auth/store";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const isClient = typeof window !== "undefined";

const value = ref("0");
const toast = useToast();

type ThirdPartyProvider = "google" | "vkontakte" | "yandex" | string;

type AuthResult =
  | { type: "THIRD_PARTY_AUTH_SUCCESS"; [key: string]: unknown }
  | { type: "THIRD_PARTY_AUTH_ERROR"; error?: string }
  | { type: "ACCOUNT_MERGED"; message?: string }
  | { type: string; [key: string]: unknown };

type ThirdPartySuccessResult = Extract<AuthResult, { type: "THIRD_PARTY_AUTH_SUCCESS" }>;

const isAuthResult = (payload: unknown): payload is AuthResult => {
  return typeof payload === "object" && payload !== null && "type" in payload;
};

const tabs = ref([
  {
    title: t("auth_wrapper.sign_in_tab"),
    value: "0",
  },
  {
    title: t("auth_wrapper.create_account_tab"),
    value: "1",
  },
]);

const handleThirdPartyLogin = async (provider: ThirdPartyProvider) => {
  try {
    await useAuthStore().thirdPartyAuth(provider);
    // Promise resolved = popup auth succeeded, handle on the main window side
    await handleThirdPartyAuthSuccess({ type: "THIRD_PARTY_AUTH_SUCCESS" });
  } catch (error) {
    const silentMessages = ['Popup closed by user', 'Third-party auth cancelled'];
    if (error instanceof Error && !silentMessages.includes(error.message)) {
      toast.add({
        severity: "error",
        summary: t("auth_wrapper.auth_error"),
        detail: error instanceof Error ? error.message : t("auth_wrapper.auth_error"),
        life: 5000,
      });
    }
  }
};

const handleThirdPartyAuthSuccess = async (data: ThirdPartySuccessResult) => {
  try {
    // Load tokens from localStorage into the main window's store
    const tokenStore = useTokenStore();
    const userTokens = isClient ? window.localStorage.getItem("userTokens") : null;
    if (userTokens) {
      const parsedTokens = JSON.parse(userTokens);
      tokenStore.accessToken = parsedTokens?.accessToken;
      tokenStore.refreshToken = parsedTokens?.refreshToken;
      tokenStore.expiresIn = parsedTokens?.expiresIn;
      tokenStore.refreshExpiresIn = parsedTokens?.refreshExpiresIn;

      // Schedule token refresh
      tokenStore.scheduleTokenRefresh();
    }

    // Get user info
    await useAuthStore().getUserInfo();

    // Show success toast
    toast.add({
      severity: "success",
      summary: t("auth_wrapper.login_success"),
      detail: t("auth_wrapper.login_success_detail"),
      life: 3000,
    });

    // Close modal
    useAuthModal().hideAuthModal();
  } catch (error) {
    console.error("Failed to complete third-party authentication:", error);
    toast.add({
      severity: "error",
      summary: t("auth_wrapper.error"),
      detail: t("auth_wrapper.auth_complete_error"),
      life: 5000,
    });
  }
};

const handleStorage = (event: StorageEvent) => {
  if (event.key !== "auth_result") {
    return;
  }

  try {
    const parsed = event.newValue ? JSON.parse(event.newValue) : null;

    if (!isAuthResult(parsed)) {
      return;
    }

    if (parsed.type === "THIRD_PARTY_AUTH_SUCCESS") {
      handleThirdPartyAuthSuccess(parsed);
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    } else if (parsed.type === "THIRD_PARTY_AUTH_ERROR") {
      toast.add({
        severity: "error",
        summary: t("auth_wrapper.auth_error"),
        detail: parsed.error || t("auth_wrapper.auth_error"),
        life: 5000,
      });
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    } else if (parsed.type === "ACCOUNT_MERGED") {
      toast.add({
        severity: "info",
        summary: t("auth_wrapper.account_merged"),
        detail: parsed.message || t("auth_wrapper.account_merged_detail"),
        life: 8000,
      });
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    }
  } catch (error) {
    console.error("Failed to parse auth result from localStorage:", error);
  }
};

onMounted(() => {
  window.addEventListener("storage", handleStorage);
});

onUnmounted(() => {
  window.removeEventListener("storage", handleStorage);
  // Abort an in-flight popup flow so its 1s poll interval dies with the modal.
  useAuthStore().cancelThirdPartyPopup();
});
</script>

<template>
  <div class="fair-auth-panel">
    <!-- FE-014 — tab row built from the redesign tokens; PrimeVue Tabs needed a
         `:dt` theme object just to look neutral. -->
    <div
      class="fair-auth-tabs"
      role="tablist"
    >
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        :class="['fair-auth-tabs__tab', value === tab.value ? 'fair-auth-tabs__tab--active' : '']"
        :aria-selected="value === tab.value"
        @click="value = tab.value"
      >
        {{ tab.title }}
      </button>
    </div>

    <SignIn v-if="value === '0'" />
    <SignUp v-else />

    <div class="fair-auth-social">
      <span class="fair-auth-social__divider">{{ t("auth_wrapper.or_sign_in_with") }}</span>
      <div class="fair-auth-social__buttons">
        <button
          type="button"
          class="fair-auth-social__button"
          aria-label="Google"
          @click="handleThirdPartyLogin('google')"
        >
          <img
            src="/src/assets/img/Google.svg"
            alt=""
          >
        </button>
        <button
          type="button"
          class="fair-auth-social__button"
          aria-label="VK"
          @click="handleThirdPartyLogin('vkontakte')"
        >
          <img
            src="/src/assets/img/VK.svg"
            alt=""
          >
        </button>
        <button
          type="button"
          class="fair-auth-social__button"
          aria-label="Yandex"
          @click="handleThirdPartyLogin('yandex')"
        >
          <img
            src="/src/assets/img/Yandex.svg"
            alt=""
          >
        </button>
      </div>
    </div>

    <p class="fair-auth-switch">
      <template v-if="value === '0'">
        {{ t("auth_wrapper.no_account") }}
        <button
          type="button"
          class="fair-auth-form__link"
          @click="value = '1'"
        >
          {{ t("auth_wrapper.create_account") }}
        </button>
      </template>
      <template v-else>
        {{ t("auth_wrapper.have_account") }}
        <button
          type="button"
          class="fair-auth-form__link"
          @click="value = '0'"
        >
          {{ t("auth_wrapper.sign_in") }}
        </button>
      </template>
    </p>
  </div>
</template>
